import { useEffect, useState } from 'react'

/**
 * Hook to ensure the Google Maps Places API is loaded.
 * First checks if the script is already loaded (e.g., by google-map-react).
 * If not loaded after a short delay, injects the script tag itself with
 * the Places library. This handles pages where no map renders.
 */
export const useEnsurePlacesLoaded = (apiKey?: string): boolean => {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const isPlacesAvailable = () =>
      typeof google !== 'undefined' && !!google.maps?.places?.Place

    if (isPlacesAvailable()) {
      setIsLoaded(true)
      return undefined
    }

    // If Google Maps hasn't been loaded yet (e.g., no map on this page),
    // load it ourselves with the Places library
    const loadScript = () => {
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        // Script tag exists but hasn't finished -- keep polling
        return false
      }
      if (!apiKey) return false

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
      script.async = true
      script.defer = true
      document.head.appendChild(script)
      return true
    }

    let attempts = 0
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const MAX_ATTEMPTS = 100 // Stop polling after ~10 seconds

    const checkLoaded = () => {
      if (isPlacesAvailable()) {
        setIsLoaded(true)
        return
      }
      attempts++
      // After 5 polls (500ms), if no Google Maps script exists, load it
      if (attempts === 5) loadScript()
      if (attempts < MAX_ATTEMPTS) {
        timeoutId = setTimeout(checkLoaded, 100)
      }
    }
    checkLoaded()

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [apiKey])

  return isLoaded
}

export default useEnsurePlacesLoaded
